import { useMemo, useState } from 'react';
import { FRICTION_SUBTYPE_OPTIONS_BY_TYPE, RESCUES } from '../constants';
import type { FrictionSubtype, InterruptionSource, PingType, RescueKind } from '../types';
import type { RecentPingContext } from '../lib/rescuePreferences';
import { BreathingBox } from './BreathingBox';

function rescueBadge(kind: RescueKind): string {
  if (kind === 'reset_2m') return '2M';
  if (kind === 'make_smaller_10m') return '10M';
  if (kind === 'focus_25m') return '25M';
  if (kind === 'breathe_90s') return 'BOX';
  if (kind === 'brain_dump_2m') return 'DUMP';
  if (kind === 'walk_5m') return 'WALK';
  if (kind === 'stand_water') return 'SIP';
  return '1S';
}

type QuickLogSheetProps = {
  pingType: PingType | null;
  subtype: FrictionSubtype | null;
  topPickKind: RescueKind | null;
  favoriteRescues: RescueKind[];
  recentContext: RecentPingContext | null;
  labels: {
    title: string;
    close: string;
    body: string;
    primaryTitle: string;
    primaryBody: string;
    optional: string;
    skip: string;
    favorites: string;
    recentContextHint: string;
    applyLastContext: string;
    alternateRescues: string;
    start: string;
  };
  subtypeLabel: (subtype: FrictionSubtype) => string;
  rescueKindLabel: (kind: RescueKind) => string;
  rescueGuide: (kind: RescueKind) => {
    summary: string;
    prompt: string | null;
    actionLabel: string;
    breathing: {
      intro: string;
      inhale: string;
      hold: string;
      exhale: string;
    };
  };
  interruptionLabel: (source: InterruptionSource) => string;
  onClose: () => void;
  onSetSubtype: (subtype: FrictionSubtype | null) => void;
  onApplyRecentContext: (context: RecentPingContext) => void;
  onStartRescue: (kind: RescueKind, note?: string | null) => void;
};

export function QuickLogSheet({
  pingType,
  subtype,
  topPickKind,
  favoriteRescues,
  recentContext,
  labels,
  subtypeLabel,
  rescueKindLabel,
  rescueGuide,
  interruptionLabel,
  onClose,
  onSetSubtype,
  onApplyRecentContext,
  onStartRescue
}: QuickLogSheetProps) {
  const [guidedDraft, setGuidedDraft] = useState('');
  const subtypeOptions = useMemo(() => (pingType ? FRICTION_SUBTYPE_OPTIONS_BY_TYPE[pingType] : []), [pingType]);
  const otherRescues = useMemo(
    () => RESCUES.map((item) => item.kind).filter((kind) => kind !== topPickKind),
    [topPickKind]
  );
  const favoriteSet = useMemo(() => new Set(favoriteRescues), [favoriteRescues]);
  const orderedRescues = useMemo(
    () => [...otherRescues.filter((kind) => favoriteSet.has(kind)), ...otherRescues.filter((kind) => !favoriteSet.has(kind))],
    [favoriteSet, otherRescues]
  );
  const topGuide = topPickKind ? rescueGuide(topPickKind) : null;

  return (
    <div className="sheet-overlay" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-header">
          <h3>{labels.title}</h3>
          <button onClick={onClose}>{labels.close}</button>
        </div>
        <div className="sheet-intro">
          <div className="small sheet-lede">{labels.body}</div>
        </div>
        {topPickKind && (
          <div className="card quick-log-featured quick-log-primary-card">
            <div className="flow-step-label">{labels.primaryTitle}</div>
            <div className="small quick-log-primary-body">{labels.primaryBody}</div>
            <div className="row-title rescue-title-with-badge">
              <span className="rescue-mini-badge" data-kind={topPickKind}>{rescueBadge(topPickKind)}</span>
              <span>{rescueKindLabel(topPickKind)}</span>
            </div>
            {topGuide && <div className="small quick-log-guide">{topGuide.summary}</div>}
            {topPickKind === 'breathe_90s' && topGuide && <BreathingBox labels={topGuide.breathing} />}
            {topGuide?.prompt && (
              topPickKind === 'make_smaller_10m' ? (
                <input
                  className="quick-log-guide-input"
                  placeholder={topGuide.prompt}
                  value={guidedDraft}
                  onChange={(e) => setGuidedDraft(e.target.value)}
                />
              ) : (
                <textarea
                  className="quick-log-guide-textarea"
                  placeholder={topGuide.prompt}
                  value={guidedDraft}
                  maxLength={280}
                  onChange={(e) => setGuidedDraft(e.target.value)}
                />
              )
            )}
            <button className="start-btn" onClick={() => onStartRescue(topPickKind, guidedDraft.trim() || null)}>
              {topGuide?.actionLabel ?? labels.start}
            </button>
          </div>
        )}
        {(recentContext || subtypeOptions.length > 0) && (
          <details className="card quick-log-optional-card">
            <summary>{labels.optional}</summary>
            <div className="stack quick-log-optional-body">
              {recentContext && (
                <div className="card rescue-context-card quick-log-context-card">
                  <div className="small quick-log-group-title">{labels.recentContextHint}</div>
                  <div className="small">
                    {[recentContext.subtype ? subtypeLabel(recentContext.subtype) : null, recentContext.interruptionSource ? interruptionLabel(recentContext.interruptionSource) : null]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                  <button className="start-btn" onClick={() => onApplyRecentContext(recentContext)}>
                    {labels.applyLastContext}
                  </button>
                </div>
              )}
              {subtypeOptions.length > 0 && (
                <div className="chips quick-log-chips">
                  {subtypeOptions.map((item) => (
                    <button key={item.subtype} className={subtype === item.subtype ? 'active' : ''} onClick={() => onSetSubtype(item.subtype)}>
                      {subtypeLabel(item.subtype)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </details>
        )}
        <details className="card quick-log-optional-card">
          <summary>{labels.alternateRescues}</summary>
          <div className="stack quick-log-optional-body">
            {favoriteRescues.length > 0 && <div className="small quick-log-group-title">{labels.favorites}</div>}
            <div className="stack quick-log-rescue-list">
              {orderedRescues.map((kind) => (
                <div key={kind} className="card rescue-row quick-log-rescue-card" data-kind={kind}>
                  <div className="stack">
                    <div className="row-title rescue-title-with-badge">
                      <span className="rescue-mini-badge" data-kind={kind}>{rescueBadge(kind)}</span>
                      <span>{rescueKindLabel(kind)}</span>
                    </div>
                    <div className="small quick-log-guide">{rescueGuide(kind).summary}</div>
                  </div>
                  <button className="start-btn" onClick={() => onStartRescue(kind, null)}>
                    {rescueGuide(kind).actionLabel}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </details>
        <button className="secondary-btn quick-log-skip-btn" onClick={() => onSetSubtype(null)}>
          {labels.skip}
        </button>
      </div>
    </div>
  );
}
